/**
 * Consommation du flux SSE de progression d'un déploiement (§3.3).
 *
 * Points sensibles traités ici :
 * - les événements sont **nommés** par le serveur (`event: snapshot`, `state`,
 *   `progress`, `log`) : on s'abonne par nom, `onmessage` ne recevrait rien ;
 * - un rechargement de page ne perd rien : le `snapshot` reconstruit l'état
 *   complet, logs compris, et il peut très bien arriver sur un job déjà
 *   terminé — auquel cas le serveur referme aussitôt le flux ;
 * - `EventSource` se reconnecte automatiquement à chaque fermeture. Sur un job
 *   terminé, cela bouclerait indéfiniment : on ferme donc explicitement dès que
 *   l'état devient terminal.
 */

import {
  isTerminal,
  type DeploymentState,
  type SseLogEvent,
  type SseProgressEvent,
  type SseSnapshotEvent,
  type SseStateEvent,
} from '@portail/shared';
import { useCallback, useEffect, useState } from 'react';

import { deploymentEventsUrl } from '../api';
import { appendLogs, mergeSnapshotLogs, type LogEntry } from '../lib/logs';

/** État reconstruit du déploiement suivi. */
export interface DeploymentStreamState {
  /** `null` tant que le premier `snapshot` n'est pas arrivé. */
  state: DeploymentState | null;
  /** Progression globale annoncée par le serveur, entre 0 et 100. */
  progress: number;
  /** Message d'étape, tel que publié par le pipeline. */
  message: string | null;
  failedStep: DeploymentState | null;
  failureReason: string | null;
  url: string | null;
  createdAt: string | null;
  finishedAt: string | null;
  logs: LogEntry[];
  /** Vrai lorsque le flux est ouvert et reçoit des événements. */
  connected: boolean;
  /** Renseigné lorsque le flux est interrompu alors que le job court encore. */
  connectionError: string | null;
}

const INITIAL_STATE: DeploymentStreamState = {
  state: null,
  progress: 0,
  message: null,
  failedStep: null,
  failureReason: null,
  url: null,
  createdAt: null,
  finishedAt: null,
  logs: [],
  connected: false,
  connectionError: null,
};

/** Analyse le corps JSON d'un événement SSE. */
function parseEvent<T>(event: MessageEvent): T | null {
  try {
    return JSON.parse(event.data as string) as T;
  } catch {
    return null;
  }
}

/**
 * Ouvre le flux d'un déploiement et en restitue l'état vivant.
 * `reconnect` force une réouverture manuelle après une coupure définitive.
 */
export function useDeploymentStream(deploymentId: string): DeploymentStreamState & {
  reconnect: () => void;
} {
  const [state, setState] = useState<DeploymentStreamState>(INITIAL_STATE);
  const [attempt, setAttempt] = useState(0);

  const reconnect = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    // Un changement d'identifiant repart d'un état vierge : sans cela, la page
    // de progression d'un nouveau job afficherait un instant l'ancien.
    setState(INITIAL_STATE);

    const source = new EventSource(deploymentEventsUrl(deploymentId));

    /** Ferme définitivement le flux, sans laisser la reconnexion automatique. */
    const close = (): void => {
      source.close();
    };

    source.addEventListener('open', () => {
      setState((previous) => ({ ...previous, connected: true, connectionError: null }));
    });

    source.addEventListener('snapshot', (event) => {
      const snapshot = parseEvent<SseSnapshotEvent>(event as MessageEvent);
      if (!snapshot) return;

      setState((previous) => ({
        ...previous,
        state: snapshot.state,
        progress: snapshot.progress,
        message: snapshot.message,
        failedStep: snapshot.failedStep,
        failureReason: snapshot.failureReason,
        url: snapshot.url,
        createdAt: snapshot.createdAt,
        finishedAt: snapshot.finishedAt,
        logs: mergeSnapshotLogs(snapshot.recentLogs, previous.logs, snapshot.seq),
        connected: true,
        connectionError: null,
      }));

      // Le serveur referme le flux juste après le snapshot d'un job terminé :
      // on l'anticipe pour éviter le cycle « fermeture → reconnexion ».
      if (isTerminal(snapshot.state)) close();
    });

    source.addEventListener('state', (event) => {
      const transition = parseEvent<SseStateEvent>(event as MessageEvent);
      if (!transition) return;

      setState((previous) => ({
        ...previous,
        state: transition.state,
        // La progression ne recule pas, même si un événement arrive dans le
        // désordre après une reconnexion.
        progress: Math.max(previous.progress, transition.progress),
        message: transition.message ?? previous.message,
        finishedAt: isTerminal(transition.state) ? transition.at : previous.finishedAt,
        // L'échec voyage désormais avec sa cause. Attendre le rechargement de la
        // fiche pour l'obtenir était une course, et le front la gagnait parfois :
        // le rapport d'erreur annonçait « étape non déterminée » sur un échec dont
        // le motif figurait dans les logs juste au-dessus.
        failedStep: transition.failedStep ?? previous.failedStep,
        failureReason: transition.failureReason ?? previous.failureReason,
      }));

      if (isTerminal(transition.state)) close();
    });

    source.addEventListener('progress', (event) => {
      const tick = parseEvent<SseProgressEvent>(event as MessageEvent);
      if (!tick) return;

      setState((previous) => ({
        ...previous,
        state: previous.state ?? tick.state,
        progress: Math.max(previous.progress, tick.progress),
      }));
    });

    source.addEventListener('log', (event) => {
      const entry = parseEvent<SseLogEvent>(event as MessageEvent);
      if (!entry) return;

      setState((previous) => ({
        ...previous,
        logs: appendLogs(previous.logs, [
          { seq: entry.seq, step: entry.step, level: entry.level, line: entry.line, at: entry.at },
        ]),
      }));
    });

    source.addEventListener('error', () => {
      setState((previous) => {
        // Sur un job terminé, la fermeture est normale : on ne l'affiche pas
        // comme un incident.
        if (previous.state && isTerminal(previous.state)) {
          return { ...previous, connected: false };
        }
        return {
          ...previous,
          connected: false,
          connectionError: 'Le suivi en direct est interrompu. Reconnexion automatique en cours…',
        };
      });
    });

    return () => {
      source.close();
    };
  }, [deploymentId, attempt]);

  return { ...state, reconnect };
}
